import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
  IconButton,
  Stack,
} from '@mui/material';
import ChevronDownIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import UserMenu from '../../components/UserMenu/UserMenu';
import {
  AppBarStyles,
  ChevronDownStyles,
  CreateBtnStyles,
  DrawerSx,
  HamburgerMenuSx,
  LogoBoxStyles,
  MenuOpenIconContainerSx,
  MenuOpenIconSx,
  NavigationLinksDesktopSx,
  NavigationLinksMobileSx,
  VerticalLineStyles,
  menuItemStyles,
  navigationLinkBtnStyles,
} from './MainNavigationProps';
import {
  getLinkIcon,
  MainLink,
  menuItems,
  navigationLinks,
} from './MainNavigationUtils';
import { LinkTitle } from './MainNavigationEnums';
import { HOME_TAB_URL, useTabNavigation } from '../../store/mainTabsStore';
import { TestIds } from '../../utils/dataTestIds';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SimpleTreeView from '../../components/SimpleTreeView/SimpleTreeView';
import TreeItem from '../../components/TreeItem/TreeItem';
import Drawer from '../../components/Drawer/Drawer';
import {
  TASK_TYPE_NAME,
  REMINDER_TYPE_NAME,
  MESSAGE_TYPE_NAME,
} from '../../pages/HomePage/components/HomePageTaskCreator/HomePageTaskCreatorProps';

const NAV_TASK_CREATOR_TYPES: Partial<Record<LinkTitle, string>> = {
  [LinkTitle.Task]: TASK_TYPE_NAME,
  [LinkTitle.Reminder]: REMINDER_TYPE_NAME,
  [LinkTitle.Message]: MESSAGE_TYPE_NAME,
};

const MainNavigation = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentMenu, setCurrentMenu] = useState<null | string>(null);
  const [anchorElBtn, setAnchorElBtn] = React.useState<null | HTMLElement>(
    null
  );
  const { openTab, makeTabActive, isTabOpen, setHomeTabData } =
    useTabNavigation();

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  function openLink(link?: MainLink) {
    if (link) {
      const presetType = NAV_TASK_CREATOR_TYPES[link.title];
      const homeTabData = presetType
        ? { presetTaskCreatorType: presetType }
        : undefined;

      if (link.url === HOME_TAB_URL) setHomeTabData(homeTabData);

      if (isTabOpen(link.url)) {
        makeTabActive(link.url);
      } else {
        openTab({
          name: link.title,
          isDirty: false,
          url: link.url,
          clientFormData: undefined,
          data: homeTabData,
        });
      }
    }
  }

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    menu: string | null
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentMenu(menu);
  };

  const handleMenuClose = (
    setter: React.Dispatch<React.SetStateAction<null | HTMLElement>>,
    link?: MainLink,
    resetMenu?: React.Dispatch<React.SetStateAction<null | string>>
  ) => {
    setter(null);
    if (resetMenu) setCurrentMenu(null);
    openLink(link);
  };

  function handleRootItemClick(item: string) {
    setExpandedItems((items) => {
      const isOpened = items.findIndex((e) => e === item) !== -1;
      if (isOpened) {
        return [];
      }

      return [item];
    });
  }

  function handleMobileLinkClick(link?: MainLink) {
    setHamburgerIsOpen(false);
    openLink(link);
  }

  const handleCreateNewClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElBtn(event.currentTarget);
  };

  const [hamburgerIsOpen, setHamburgerIsOpen] = useState(false);

  return (
    <AppBar
      className="main-navigation"
      position="static"
      sx={AppBarStyles}
      data-testid={TestIds.MainNavigation.Container}
    >
      <Toolbar>
        <Box sx={LogoBoxStyles}>
          <a
            className="logo"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              makeTabActive('/');
            }}
          >
            <img
              src={`${import.meta.env.VITE_DO_SPACE_URL}/assets/taskit-logo.png`}
              alt="logo"
            />
          </a>
        </Box>
        <Box sx={NavigationLinksDesktopSx}>
          {navigationLinks.map((nav) => (
            <React.Fragment key={nav.title}>
              <Button
                color="inherit"
                sx={navigationLinkBtnStyles}
                onClick={(event) => handleMenuClick(event, nav.title)}
              >
                {nav.title}
                <ChevronDownIcon
                  sx={{
                    ...ChevronDownStyles,
                    transform:
                      currentMenu === nav.title
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                  }}
                />
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={currentMenu === nav.title}
                onClose={() =>
                  handleMenuClose(setAnchorEl, undefined, setCurrentMenu)
                }
                sx={{ marginTop: '10px' }}
              >
                {nav.links.map((link) => (
                  <MenuItem
                    sx={menuItemStyles}
                    key={link?.title}
                    onClick={() =>
                      handleMenuClose(setAnchorEl, link, setCurrentMenu)
                    }
                  >
                    {link?.title}
                  </MenuItem>
                ))}
              </Menu>
            </React.Fragment>
          ))}
        </Box>
        <Box sx={NavigationLinksMobileSx}>
          <IconButton onClick={() => setHamburgerIsOpen(true)}>
            <MenuIcon sx={{ fill: 'white' }} />
          </IconButton>

          <Drawer
            open={hamburgerIsOpen}
            onClose={() => setHamburgerIsOpen(false)}
            sx={DrawerSx}
          >
            <Box sx={HamburgerMenuSx} role="presentation">
              <Stack sx={MenuOpenIconContainerSx}>
                <IconButton onClick={() => setHamburgerIsOpen(false)}>
                  <MenuOpenIcon sx={MenuOpenIconSx} />
                </IconButton>
              </Stack>
              <SimpleTreeView expandedItems={expandedItems}>
                {navigationLinks.map((nav) => (
                  <TreeItem
                    itemId={nav.title}
                    label={nav.title}
                    key={nav.title}
                    onClick={() => handleRootItemClick(nav.title)}
                  >
                    {nav.links.map((link) => (
                      <TreeItem
                        key={link?.title}
                        itemId={link?.title}
                        label={link?.title}
                        onClick={() => handleMobileLinkClick(link)}
                      />
                    ))}
                  </TreeItem>
                ))}
              </SimpleTreeView>
            </Box>
          </Drawer>
        </Box>
        <Box sx={{ marginLeft: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateNewClick}
            startIcon={<AddIcon />}
            sx={CreateBtnStyles}
          >
            Create new
          </Button>

          <Box sx={{ flexShrink: '0' }}>
            <Menu
              anchorEl={anchorElBtn}
              open={Boolean(anchorElBtn)}
              onClose={() => handleMenuClose(setAnchorElBtn)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              sx={{ marginTop: '10px' }}
            >
              {menuItems
                .filter((el) => el !== undefined)
                .map((item) => (
                  <MenuItem
                    sx={{ width: '220px' }}
                    key={item.title}
                    onClick={() => handleMenuClose(setAnchorElBtn, item)}
                  >
                    <ListItemIcon sx={{ marginRight: '-5px' }}>
                      {getLinkIcon(item)}
                    </ListItemIcon>
                    <ListItemText primary={item.menuLabel ?? item.title} />
                  </MenuItem>
                ))}
            </Menu>
          </Box>
          <Box sx={VerticalLineStyles}></Box>
        </Box>

        <UserMenu />
      </Toolbar>
    </AppBar>
  );
};

export default MainNavigation;
